import { useEffect, useState, useCallback, useMemo } from "react";
import {
    Stack,
    Text,
    Spinner,
    SpinnerSize,
    mergeStyleSets,
    IStackTokens,
    MessageBar,
    MessageBarType,
    Dropdown,
    IDropdownOption,
} from "@fluentui/react";
import GameCard from "./GameCard";
import StandingsSection from "./StandingsSection";
import BracketGridSection from "./BracketGridSection";
import BackToTop from "./BackToTop";

export interface ITeamScore {
    score: number;
    bonusPoints: number;
    bonusesHeard?: number;
}

export interface IGameEntry {
    phase: string;
    bracket: string;
    round: string;
    room?: string;
    moderator?: string;
    startTime?: string;
    isFinished: boolean;
    currentQuestion: number;
    lastUpdated?: string;
    tiebreaker?: boolean;
    teams: Record<string, ITeamScore>;
}

export interface ITeam {
    name: string;
    logoUrl?: string;
}

export interface IBracket {
    name: string;
    teamIds: string[];
}

export interface IPhase {
    name: string;
    order?: number;
    noCarryovers?: boolean;
    brackets: IBracket[];
}

export interface IScoreboardData {
    tournamentName?: string;
    statsUrl?: string;
    complete?: boolean;
    teamDictionary?: Record<string, ITeam>;
    games?: Record<string, IGameEntry>;
    phases?: IPhase[];
}

export interface ILiveScoreboardProps {
    /** Polling interval in milliseconds. Defaults to 30000 (30 seconds). */
    pollInterval?: number;
    /** Title to display at the top of the scoreboard */
    title?: string;
}

const styles = mergeStyleSets({
    container: {
        padding: 20,
        maxWidth: 1200,
        margin: "0 auto",
    },
    title: {
        fontSize: 28,
        fontWeight: 600,
        marginBottom: 4,
        textAlign: "center" as const,
    },
    statsLink: {
        fontSize: 14,
        textAlign: "center" as const,
        marginBottom: 16,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 16,
        "@media (max-width: 900px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        "@media (max-width: 600px)": {
            gridTemplateColumns: "1fr",
        },
    },
    noGames: {
        textAlign: "center" as const,
        padding: 40,
        color: "#666",
    },
    refreshInfo: {
        fontSize: 12,
        color: "#666",
        textAlign: "center" as const,
        marginTop: 20,
    },
    gamesHeader: {
        fontSize: 24,
        fontWeight: 600,
        marginBottom: 4,
        paddingBottom: 8,
        borderBottom: "2px solid #0078d4",
    },
    navDropdown: {
        maxWidth: 350,
        alignSelf: "center" as const,
    },
});

const stackTokens: IStackTokens = { childrenGap: 8 };

export default function LiveScoreboard({
    pollInterval = 30000,
    title = "2026 ACF Nationals Scoreboard",
}: ILiveScoreboardProps) {
    const [scoreboardData, setScoreboardData] = useState<IScoreboardData>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchGames = useCallback(async () => {
        try {
            const response = await fetch("/api/games");
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data: IScoreboardData = await response.json();
            setScoreboardData(data);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch games:", err);
            setError(err instanceof Error ? err.message : "Failed to load games");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGames();

        if (scoreboardData.complete) {
            return;
        }

        let interval = setInterval(fetchGames, pollInterval);

        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearInterval(interval);
            } else {
                fetchGames();
                interval = setInterval(fetchGames, pollInterval);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [fetchGames, pollInterval, scoreboardData.complete]);

    const ongoingGames = useMemo(() => {
        const games = scoreboardData.games ?? {};
        const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
        return Object.entries(games)
            .filter(([, game]) => !game.isFinished
                || (game.lastUpdated && new Date(game.lastUpdated).getTime() > tenMinutesAgo))
            .sort((a, b) => a[1].round.localeCompare(b[1].round));
    }, [scoreboardData]);

    const teamDictionary = scoreboardData.teamDictionary ?? {};
    const games = scoreboardData.games ?? {};
    const phases = scoreboardData.phases ?? [];

    const navOptions = useMemo(() => {
        const options: IDropdownOption[] = [
            { key: "ongoing-games", text: "Ongoing and Recent Games" },
        ];
        const sortedPhases = [...phases].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
        for (const phase of sortedPhases) {
            options.push({
                key: `standings-${phase.name.toLowerCase().replace(/\s+/g, "-")}`,
                text: `${phase.name} Standings`,
            });
            options.push({
                key: `grid-${phase.name.toLowerCase().replace(/\s+/g, "-")}`,
                text: `${phase.name} Bracket Grid`,
            });
        }
        return options;
    }, [phases]);

    if (loading) {
        return (
            <Stack className={styles.container} horizontalAlign="center" verticalAlign="center">
                <Spinner size={SpinnerSize.large} label="Loading games..." />
            </Stack>
        );
    }

    const tournamentTitle = scoreboardData.tournamentName || title;

    return (
        <Stack id="scoreboard-top" className={styles.container} tokens={stackTokens}>
            <Text className={styles.title}>{tournamentTitle} Live Scoreboard</Text>
            {scoreboardData.statsUrl && (() => {
                let domain: string;
                try {
                    domain = new URL(scoreboardData.statsUrl).hostname;
                } catch {
                    domain = scoreboardData.statsUrl;
                }
                return (
                    <Text className={styles.statsLink} block>
                        Standings are not final and may be inaccurate. See more team and individual stats on{" "}
                        <a href={scoreboardData.statsUrl} target="_blank" rel="noopener noreferrer">
                            {domain}
                        </a>
                    </Text>
                );
            })()}
            {navOptions.length > 1 && (
                <Dropdown
                    placeholder="Jump to section..."
                    options={navOptions}
                    className={styles.navDropdown}
                    onChange={(_, option) => {
                        if (option) {
                            const el = document.getElementById(option.key as string);
                            el?.scrollIntoView({ behavior: "smooth" });
                        }
                    }}
                />
            )}
            <Text id="ongoing-games" className={styles.gamesHeader} block>
                Ongoing and Recent Games
            </Text>
            {error && <MessageBar messageBarType={MessageBarType.error}>Error loading games: {error}</MessageBar>}

            {ongoingGames.length === 0 ? (
                <Text className={styles.noGames}>No ongoing games at the moment.</Text>
            ) : (
                <div className={styles.grid}>
                    {ongoingGames.map(([gameKey, game]) => (
                        <GameCard
                            key={gameKey}
                            gameKey={gameKey}
                            game={game}
                            teamDictionary={teamDictionary}
                        />
                    ))}
                </div>
            )}

            <BackToTop />

            {phases.length > 0 && (
                <StandingsSection
                    games={games}
                    phases={phases}
                    teamDictionary={teamDictionary}
                />
            )}

            {phases.length > 0 && (
                <BracketGridSection
                    games={games}
                    phases={phases}
                    teamDictionary={teamDictionary}
                />
            )}

            {!scoreboardData.complete && (
                <Text className={styles.refreshInfo}>Auto-refreshing every {pollInterval / 1000} seconds</Text>
            )}
        </Stack>
    );
};